/**
 * `cai gui` launcher — locator only, never bundles the GUI.
 *
 * The CLI carries no Tauri/Rust/frontend code. This module just resolves the
 * project (via the existing scaffold resolution) and spawns the separately-
 * installed GUI binary, erroring with installation guidance when it's absent
 * for the current platform.
 */

import { existsSync } from "node:fs";
import { join } from "node:path";
import { spawn } from "node:child_process";

/** Platform-specific GUI binary/app names produced by the Tauri build. */
export function guiBinaryCandidates(platform: NodeJS.Platform = process.platform): string[] {
  switch (platform) {
    case "darwin":
      // Product name is "CAI" → CAI.app; the inner binary keeps the cai-gui
      // crate name. Older "CAI Sidecar.app" kept as a fallback for existing builds.
      return [
        "CAI.app/Contents/MacOS/cai-gui",
        "CAI Sidecar.app/Contents/MacOS/cai-gui",
        "cai-gui",
      ];
    case "win32":
      return ["CAI.exe", "cai-gui.exe", "CAI Sidecar.exe"];
    default:
      return ["cai-gui"];
  }
}

/**
 * Search the known install locations for the GUI binary. Honors
 * `CAI_GUI_BIN` as an explicit override (used in dev / CI). Returns the first
 * existing path, or null.
 */
export function resolveGuiBinary(
  env: NodeJS.ProcessEnv = process.env,
  platform: NodeJS.Platform = process.platform
): string | null {
  if (env.CAI_GUI_BIN && existsSync(env.CAI_GUI_BIN)) return env.CAI_GUI_BIN;

  const candidates = guiBinaryCandidates(platform);
  const searchDirs = [
    env.CAI_GUI_DIR,
    join(process.cwd(), "gui", "src-tauri", "target", "release"),
    join(process.cwd(), "gui", "src-tauri", "target", "release", "bundle", "macos"),
  ].filter(Boolean) as string[];

  for (const dir of searchDirs) {
    for (const name of candidates) {
      const full = join(dir, name);
      if (existsSync(full)) return full;
    }
  }
  return null;
}

export function installGuidance(platform: NodeJS.Platform = process.platform): string {
  const target = platform === "win32" ? "Windows" : platform === "darwin" ? "macOS" : "your platform";
  return [
    `The CAI sidecar GUI is not installed for ${target}.`,
    "",
    "The GUI ships separately from the CLI to keep the npm package light.",
    "Build it from the gui/ workspace (requires the Rust + Tauri toolchain):",
    "  cd gui && npm install && npm run build",
    "or set CAI_GUI_BIN to an existing binary.",
  ].join("\n");
}

export interface LaunchResult {
  binary: string;
  projectDir: string;
}

/**
 * Resolve the project and spawn the GUI in it. Throws with install guidance if
 * the binary is missing. `projectDir` comes from the caller (already resolved
 * via findConfig) so this stays free of scaffold-resolution logic.
 */
export function launchGui(
  projectDir: string,
  deps: {
    resolve?: typeof resolveGuiBinary;
    spawnFn?: typeof spawn;
  } = {}
): LaunchResult {
  const resolve = deps.resolve ?? resolveGuiBinary;
  const spawnFn = deps.spawnFn ?? spawn;

  const binary = resolve();
  if (!binary) {
    throw new Error(installGuidance());
  }

  const child = spawnFn(binary, [], {
    cwd: projectDir,
    detached: true,
    stdio: "ignore",
  });
  child.unref();

  return { binary, projectDir };
}

/**
 * Resolve the GUI binary, downloading it from the public dist repo if it isn't
 * installed locally. Set `CAI_GUI_NO_DOWNLOAD=1` to opt out (errors with the
 * manual build guidance instead). Returns the resolved binary path.
 */
export async function resolveOrDownloadGui(
  env: NodeJS.ProcessEnv = process.env,
  deps: {
    resolve?: typeof resolveGuiBinary;
    download?: () => Promise<string>;
  } = {}
): Promise<string> {
  const resolve = deps.resolve ?? resolveGuiBinary;

  const local = resolve(env);
  if (local) return local;

  if (env.CAI_GUI_NO_DOWNLOAD === "1") {
    throw new Error(installGuidance());
  }

  const download =
    deps.download ??
    (async () => {
      const { ensureGuiDownloaded } = await import("./download.js");
      return ensureGuiDownloaded();
    });

  try {
    return await download();
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    throw new Error(`${installGuidance()}\n\nAuto-download failed: ${reason}`);
  }
}

/** Async launch that auto-downloads the GUI when it's missing. */
export async function launchGuiWithDownload(
  projectDir: string,
  env: NodeJS.ProcessEnv = process.env,
  deps: {
    resolve?: typeof resolveGuiBinary;
    download?: () => Promise<string>;
    spawnFn?: typeof spawn;
  } = {}
): Promise<LaunchResult> {
  const binary = await resolveOrDownloadGui(env, deps);
  return launchGui(projectDir, { resolve: () => binary, spawnFn: deps.spawnFn });
}
